import React, { useState, useEffect } from 'react'
import { WellStratumFieldsView } from '../../../../../../../../types/form-types'
import { useGetWellData } from '../../../../../../../hooks/use-api'
import FractionCard from '../../../../../../../components/well-forms/lithology/fraction-card'
import WellViewButtons from '../../../../../../../components/wells/well-view-buttons'
import { fractionTypes } from '../../../../../../../components/well-forms/lithology/lithology-types'
import NotesWrapperView from '../../../../../../../components/well-forms/notes/notes-wrapper-view'
import { TOOLTIP } from '../../../../../../../../constants/tooltips'
import { useParamsOr404 } from '../../../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  Divider,
  FormField,
  Heading,
  InlineNotification,
  ReadOnlyField,
  RecordContent,
  RecordFooter,
  useTitleChange,
} from '@msaf/core-react'

export default function WellStratumView() {
  useTitleChange('View Well | Lithology')

  const { id, stratumId } = useParamsOr404('id', 'stratumId')
  const [fields, setFields] = useState<Partial<WellStratumFieldsView>>({})
  const navigate = useNavigate()
  const url = `wells/${id}/stratum/${stratumId}/view`
  const backUrl = `/wells/${id}/view/lithology`
  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data?.state) {
      setFields(data.state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.state])

  return (
    <>
      <RecordContent>
        <ButtonsContainer containerStyle='left'>
          <Button
            onClick={() => navigate(backUrl, { state: { activeTab: 1 } })}
            label='Back'
            iconAlignment='left'
            icon='arrow-left'
            buttonStyle='text-action'
            isSkeleton={isLoading}
          />
        </ButtonsContainer>

        <Heading level={2} isSkeleton={isLoading}>
          Stratum
        </Heading>

        <Heading level={3}>Identifiers</Heading>

        <FormField isSkeleton={isLoading} labelId='topLevel' label='Top level of stratum (m)'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='topLevel' value={fields.stratumCalculated?.stratumTop} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='bottomLevel' label='Bottom level of stratum (m)'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='bottomLevel' value={fields.stratumBottom} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='thickness' label='Stratum thickness (m)'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='thickness'
            value={fields.stratumCalculated?.stratumThickness}
          />
        </FormField>

        <FormField
          toolTipMessage={TOOLTIP.drillerStratumDescription}
          isSkeleton={isLoading}
          labelId='description'
          label='Driller&#39;s description'
        >
          <ReadOnlyField isSkeleton={isLoading} labelledBy='description' value={fields.drillerStratumDescription} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='generatedStratumDescription' label='Generated description'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='generatedStratumDescription'
            value={fields.generatedStratumDescription}
          />
        </FormField>

        {/* TODO Tooltip */}
        <FormField
          isSkeleton={isLoading}
          labelId='geologicalFormationDescription'
          label='Geological formation description'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='geologicalFormationDescription'
            value={fields.geologicalFormation}
          />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='moistureCondition' label='Moisture condition'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='moistureCondition' value={fields.moistureCondition} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='majorColour' label='Primary colour'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='majorColour' value={fields.majorColour} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='secondaryColour' label='Secondary colour'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='secondaryColour' value={fields.secondaryColour} />
        </FormField>

        <FormField isSkeleton={isLoading} labelId='relativeDensity' label='Relative density'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='relativeDensity' value={fields.relativeDensity} />
        </FormField>

        {/* TODO SVG */}
        {/* <Card>TODO SVG</Card> */}
        <Divider />
        <Heading level={3}>Fractions</Heading>

        {/* If there aren't any fraction types valid display notification */}
        {!fractionTypes.some((fractionType) => {
          const value = fields[`${fractionType}Fraction` as keyof typeof fields]
          if (`${fractionType}Fraction` in fields && value) {
            return true
          }
          return false
        }) && (
          <InlineNotification messageType='info' isDismissable={false}>
            There is no fraction information available for this stratum record
          </InlineNotification>
        )}

        {fractionTypes.map((fractionType) => {
          const value = fields[`${fractionType}Fraction` as keyof typeof fields]
          if (`${fractionType}Fraction` in fields && value) {
            return (
              <FractionCard key={fractionType} fractionType={fractionType} stratum={fields} isLoading={isLoading} />
            )
          } else {
            // Required for conditional .map return value.
            return <React.Fragment key={fractionType}></React.Fragment>
          }
        })}
      </RecordContent>
      <RecordFooter
        notes={
          Array.isArray(fields.notes) &&
          !!fields.notes.length && <NotesWrapperView notes={fields.notes} isLoading={isLoading} />
        }
        areActionsFixed
      >
        <WellViewButtons wellId={id} />
      </RecordFooter>
    </>
  )
}
